import React from 'react';
import {
  TouchableOpacity,
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ViewStyle,
  TextStyle,
} from 'react-native';
import type { LucideIcon } from 'lucide-react-native';
import { useTheme, SPACING, FONT_SIZES, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER, CURSOR_DISABLED } from '../../utils/webStyles';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'progress' | 'outline';
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  /** xs: a 28px pill (the 20px button line plus 3px padding and the 1px
   *  border, twice) for row actions in dense lists (plan feature rows). */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
  fullWidth?: boolean;
  // Override when the visible title isn't enough context for screen readers
  // (e.g. icon-only buttons that pass an empty `title`).
  accessibilityLabel?: string;
  accessibilityHint?: string;
  testID?: string;
}

// Plaud grammar: primary is the ink pill; danger is BORDERED (risk text on
// a quiet fill), not solid — destructive actions whisper until confirmed.
const SOLID_VARIANTS = new Set(['primary', 'progress']);

// forwardRef (Sept 2026, panel-system.md Phase 1): PanelStack's `pop()`
// restores focus to whatever `triggerRef` a caller passed when it pushed a
// drill-in (e.g. DefensePlanAssignForm's People button) — that needs a real
// ref onto the underlying TouchableOpacity, so this can't stay a plain
// function component. Every existing call site that passes no ref is
// unaffected.
export const Button = React.forwardRef<React.ElementRef<typeof TouchableOpacity>, ButtonProps>(
  function Button(
    {
      title,
      onPress,
      variant = 'primary',
      size = 'md',
      disabled = false,
      loading = false,
      style,
      textStyle,
      fullWidth = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      accessibilityLabel,
      accessibilityHint,
      testID,
    }: ButtonProps,
    ref: React.Ref<React.ElementRef<typeof TouchableOpacity>>
  ) {
    const { colors } = useTheme();
    const isInert = disabled || loading;
    const { hovered, hoverProps } = useWebHover(!isInert);
    const isSolid = !disabled && SOLID_VARIANTS.has(variant);

    const getBackgroundColor = () => {
      if (disabled) return colors.border;
      switch (variant) {
        case 'primary':
          return colors.action;
        case 'secondary':
          return hovered ? colors.hoverSurface : colors.background;
        case 'ghost':
          return hovered ? colors.hoverSurface : 'transparent';
        case 'danger':
          return hovered ? colors.dangerSoft : colors.background;
        case 'progress':
          return colors.progress;
        case 'outline':
          return hovered ? colors.hoverSurface : 'transparent';
        default:
          return colors.action;
      }
    };

    const getTextColor = () => {
      if (disabled) return colors.textTertiary;
      switch (variant) {
        case 'primary':
          return colors.actionOn;
        case 'danger':
          return colors.danger;
        case 'secondary':
        case 'ghost':
        case 'outline':
          return colors.text;
        case 'progress':
          return colors.white;
        default:
          return colors.actionOn;
      }
    };

    const getBorderColor = () => {
      if (variant === 'secondary') return colors.borderStrong;
      if (variant === 'ghost') return 'transparent';
      if (variant === 'outline') return colors.borderStrong;
      if (variant === 'danger') return colors.dangerBorder;
      return 'transparent';
    };

    // Pills want a touch more horizontal room than the old rounded rects.
    // xs/sm share the buttonSmall label register; md/lg use `button` (lg
    // only bumps the size, keeping button's lineHeight).
    const sizeStyles = {
      xs: { paddingVertical: 3, paddingHorizontal: 12, text: TYPOGRAPHY.buttonSmall },
      sm: { paddingVertical: 8, paddingHorizontal: 12, text: TYPOGRAPHY.buttonSmall },
      md: { paddingVertical: 12, paddingHorizontal: 16, text: TYPOGRAPHY.button },
      lg: {
        paddingVertical: 16,
        paddingHorizontal: 22,
        text: { ...TYPOGRAPHY.button, fontSize: FONT_SIZES.md },
      },
    };

    return (
      <TouchableOpacity
        ref={ref}
        onPress={onPress}
        disabled={isInert}
        activeOpacity={0.8}
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? title}
        accessibilityHint={accessibilityHint}
        accessibilityState={{ disabled: isInert, busy: loading }}
        {...hoverProps}
        // @ts-expect-error dataSet is a react-native-web prop (drives the
        // press-scale rule in motion.ts's MOTION_CSS_VARS, and tells
        // audit-design-consistency.mjs the declared size and variant); not in
        // RN's type defs.
        dataSet={{ rediPress: 'true', rediButton: size, rediVariant: variant }}
        style={[
          styles.button,
          {
            backgroundColor: getBackgroundColor(),
            borderColor: getBorderColor(),
            paddingVertical: sizeStyles[size].paddingVertical,
            paddingHorizontal: sizeStyles[size].paddingHorizontal,
          },
          fullWidth && styles.fullWidth,
          disabled ? CURSOR_DISABLED : CURSOR_POINTER,
          style,
        ]}>
        {/* Hover state layer for solid fills — a wash over the fill keeps the
          label's contrast intact in both themes. */}
        {isSolid && hovered && (
          <View
            pointerEvents="none"
            style={[styles.stateLayer, { backgroundColor: colors.fillHover }]}
          />
        )}
        {loading ? (
          <ActivityIndicator color={getTextColor()} size="small" />
        ) : (
          <>
            {LeftIcon && (
              <LeftIcon
                size={size === 'sm' || size === 'xs' ? 12 : size === 'lg' ? 16 : 14}
                color={getTextColor()}
                style={title ? { marginRight: SPACING.xs + SPACING.xxs } : undefined}
              />
            )}
            {title ? (
              <Text
                style={[
                  sizeStyles[size].text,
                  {
                    color: getTextColor(),
                  },
                  textStyle,
                ]}>
                {title}
              </Text>
            ) : null}
            {RightIcon && (
              <RightIcon
                size={size === 'sm' || size === 'xs' ? 12 : size === 'lg' ? 16 : 14}
                color={getTextColor()}
                style={title ? { marginLeft: SPACING.xs + SPACING.xxs } : undefined}
              />
            )}
          </>
        )}
      </TouchableOpacity>
    );
  }
);

const styles = StyleSheet.create({
  button: {
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  fullWidth: {
    width: '100%',
  },
  stateLayer: {
    ...StyleSheet.absoluteFillObject,
  },
});
